import { useSuspenseQuery } from "@tanstack/react-query";
import {
  AuthConfig,
  cn,
  Control,
  getControlCategory,
  getControlCategoryName,
  isDataControl,
  queries,
  useAuthConfig,
  ValidControlCategory,
} from "@theprelude/core";
import { ShieldIcon } from "@theprelude/ds";
import { createQuery } from "@/lib/create-query";
import { useExceptedFilters } from "@/queries/control-monitor";
import { Card, CardHeader, Metric, MetricSet } from "../sections";
import { SummaryIconBubble } from "../summary-icon-bubbles";

interface Props {
  category: ValidControlCategory;
}

export const ControlPoliciesCard = ({ category }: Props) => {
  const authConfig = useAuthConfig();
  const { showExcepted } = useExceptedFilters();

  const { data: account } = useSuspenseQuery(
    queries.iam.getAccount(authConfig),
  );
  const controls = account.controls.filter(
    (control) =>
      getControlCategory(control.id) === category && !isDataControl(control.id),
  );
  const {
    data: { policyCount, averageIssueCount, maxIssueCount },
  } = policiesQuery({
    authConfig,
    controls,
    showExcepted,
  }).useSuspenseQuery();

  return (
    <Card className="flex flex-col gap-2">
      <CardHeader>
        <SummaryIconBubble>
          <ShieldIcon />
        </SummaryIconBubble>
        {getControlCategoryName(category)} Policies
      </CardHeader>
      <div className="flex flex-col gap-4">
        <MetricSet>
          <Metric
            label="Active policies"
            count={policyCount}
            countClassName="text-default font-normal"
          />
          <Metric
            label="Average issues"
            count={averageIssueCount}
            countClassName={cn(
              "font-normal",
              averageIssueCount > 0
                ? "text-status-yellow"
                : "text-status-green",
            )}
          />
          <Metric label="Max issues" count={maxIssueCount} />
        </MetricSet>
      </div>
    </Card>
  );
};

const policiesQuery = ({
  authConfig,
  controls,
  showExcepted,
}: {
  authConfig: AuthConfig;
  controls: Control[];
  showExcepted: boolean;
}) =>
  createQuery({
    queries: controls.map((c) =>
      queries.scm.policies(authConfig, c.id, c.instance_id),
    ),
    queryFn: (...policyResponses) => {
      const policies = policyResponses.flatMap((r) => [
        ...(r.endpoint_evaluation?.policies ?? []),
        ...(r.inbox_evaluation?.policies ?? []),
        ...(r.user_evaluation?.policies ?? []),
      ]);

      const policyCount = policies.length;

      const policyIssueCounts = policies.map(
        (p) =>
          p.settings.filter(
            (s) => !s.meets_recommendation && (showExcepted || !s.excepted),
          ).length,
      );

      const averageIssueCount =
        policyCount > 0
          ? Math.round(
              policyIssueCounts.reduce((sum, current) => sum + current, 0) /
                policyCount,
            )
          : 0;

      const maxIssueCount = policyIssueCounts.reduce(
        (max, current) => Math.max(max, current),
        0,
      );

      return { policyCount, averageIssueCount, maxIssueCount };
    },
  });
